<template>
  <div class="row">
    <div class="col-12">
      <h2>{{ $t('contracts.contract') }} {{ contract?.title }}</h2>

      <div class="my-3" v-if="contract">
        <ShowRow :label="$t('clients.client')" :value="`${clientStore.client?.kdnr} ${clientStore.client?.name}`"/>
        <ShowRow :label="$t('contracts.contract_date')" :value="formatDate(contract.contract_date, 'short2')"/>
        <ShowRow :label="$t('contracts.description')" :value="contract.description"/>
        <ShowRow :label="$t('contracts.contract_status')"
                 :value="setupStore.getContractStatusById(contract.contract_status_id)?.name"/>
        <ShowRow :label="$t('contracts.operations')">
          <a :href="`https://crm.zoho.eu/crm/org20062499282/tab/SalesOrders/${contract?.zoho_sales_order_id}`"
             target="_blank" class="color-inherit">
            <i class="fas fa-handshake"/>
          </a>
        </ShowRow>
        <ShowRow :label="$t('contracts.info')" :value="contract.info"/>
        <ShowRow :label="$t('contracts.zoho_infos')">
          <CollapsedInfos :data="contract.zoho_infos" />
        </ShowRow>
        <ShowRow :label="$t('contracts.general_terms')">
          <div v-if="generalTermAssignments.length" class="table-responsive fw-normal">
            <table class="table table-sm align-middle mb-0">
              <thead>
              <tr>
                <th>{{ $t('general_terms.title') }}</th>
                <th>{{ $t('general_terms.key') }}</th>
                <th>{{ $t('general_terms.locked_at') }}</th>
                <th>{{ $t('contracts.general_term_sent_at') }}</th>
                <th>{{ $t('contracts.general_term_accepted_at') }}</th>
                <th class="text-end">{{ $t('contracts.operations') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="assignment in generalTermAssignments" :key="assignment.id">
                <td>{{ assignment.general_term?.title || '-' }}</td>
                <td>{{ assignment.general_term?.key || '-' }}</td>
                <td>{{ formatDate(assignment.general_term?.locked_at, 'datetime') || '-' }}</td>
                <td>{{ formatDate(assignment.sent_at, 'datetime') || '-' }}</td>
                <td class="accepted-at-cell">
                  <div class="accepted-at-editor">
                    <DatePicker v-model="acceptedAtDrafts[assignment.id]"
                                @update:model-value="updateAcceptedAt(assignment, $event)"
                                :disabled="savingAcceptedAt[assignment.id]"
                                :locale="$route.params.locale"
                                :format-locale="de"
                                format="dd.MM.yyyy"
                                placeholder="-"
                                text-input
                                :enable-time-picker="false"
                                teleport="body"/>
                    <i v-if="savingAcceptedAt[assignment.id]"
                       class="fas fa-spinner fa-spin accepted-at-saving"
                       :title="$t('contracts.general_term_accepted_at_saving')"/>
                  </div>
                </td>
                <td class="text-end">
                  <router-link v-if="assignment.general_term?.id"
                               class="btn btn-primary btn-sm"
                               :to="{name: ROUTES.GENERAL_TERMS.SHOW, params: {id: assignment.general_term.id}}"
                               :title="$t('general_terms.show')">
                    <i class="fas fa-eye"/>
                  </router-link>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <span v-else class="text-muted fw-normal">{{ $t('contracts.no_general_terms') }}</span>
        </ShowRow>

        <router-link class="btn btn-primary me-3" :to="{name: ROUTES.CONTRACTS.EDIT, params: {id: contract.id}}"
                     :title="$t('contracts.edit.headline')">
          <i class="fas fa-edit"/>
        </router-link>

        <button class="btn btn-primary me-3" @click.prevent="setOnline" :disabled="!allowSetOnline">
          {{ $t('products.setOnline') }}
        </button>
      </div>
      <!--      {{ contract }}-->

      <template v-if="contract">
        <ProductsTable :value="contract.products"/>
      </template>

      <template v-if="clientEvents">
        <ClientEventTimeline :items="clientEvents" :allow-comment="true"/>
      </template>
      <template v-if="contract?.id">
        <ClientEventAppendButton :eventable-type="API_CLASSES.CONTRACT" :eventable-id="contract.id" />
      </template>
    </div>
  </div>
</template>

<script>
import {useClientStore} from "@/stores/client";
import {useContractStore} from "@/stores/contract";
import ShowRow from "@/components/ShowRow.vue";
import {useSetupStore} from "@/stores/setup";
import ProductsTable from "@/components/tables/ProductsTable.vue";
import ClientEventTimeline from "@/components/ClientEventTimeline.vue";
import {Helpers} from "@/helpers";
import {API_CLASSES, NANO_EVENT_TYPES, ROUTES} from "@/scripts/constants";
import CollapsedInfos from "@/components/CollapsedInfos.vue";
import ClientEventAppendButton from "@/components/ClientEventAppendButton.vue";
import dayjs from "dayjs";
import {de} from "date-fns/locale";

export default {
  name: 'ClientsShow',
  components: {
    ClientEventAppendButton,
    CollapsedInfos,
    ClientEventTimeline,
    ShowRow,
    ProductsTable,
  },
  setup() {
    return {
      clientStore: useClientStore(),
      contractStore: useContractStore(),
      setupStore: useSetupStore(),
    }
  },
  data() {
    return {
      contract: null,
      clientEvents: null,
      emitListenerUnbind: null,
      acceptedAtDrafts: {},
      savingAcceptedAt: {},
    }
  },
  created() {
    this.setupStore.getContractStatuses()
    this.emitListenerUnbind = Helpers.emitter.on(NANO_EVENT_TYPES.CLIENT_EVENT_CHANGED, this.load)
    this.emitListenerUnbind2 = Helpers.emitter.on(NANO_EVENT_TYPES.CONTRACT_CHANGED, this.load)
    this.load()
  },
  beforeUnmount() {
    this.emitListenerUnbind()
    this.emitListenerUnbind2()
  },
  computed: {
    API_CLASSES() {
      return API_CLASSES
    },
    ROUTES() {
      return ROUTES
    },
    de() {
      return de
    },
    contractId() {
      return this.$route.params.id
    },
    contractStatus() {
      return this.contract?.contract_status_id ?
          this.setupStore.getContractStatusById(this.contract.contract_status_id) : null
    },
    allowSetOnline() {
      return this.contractStatus?.allow_set_online
    },
    generalTermAssignments() {
      return [...(this.contract?.contract_general_term_assignments || [])].sort((a, b) => {
        const aKey = a.general_term?.key || ''
        const bKey = b.general_term?.key || ''
        return aKey.localeCompare(bKey)
      })
    },
  },
  methods: {
    load() {
      this.contractStore.get(this.contractId).then(({data}) => {
        this.contract = data.contract
        this.acceptedAtDrafts = Object.fromEntries(
            (this.contract.contract_general_term_assignments || []).map((assignment) => [
              assignment.id,
              assignment.accepted_at ? new Date(assignment.accepted_at) : null,
            ])
        )
        this.client = data.client
        this.clientEvents = data.client_events
        this.setCurrentClient(this.client)
        this.setTitle(this.$t('contracts.contract'))
      })
    },
    setOnline() {
      const contractStatusId = this.setupStore.contractStatuses.find((ps) => ps.key === 'active').id
      this.contractStore.setOnline(this.contractId, contractStatusId)
    },
    async updateAcceptedAt(assignment, acceptedAt) {
      const previousAcceptedAt = assignment.accepted_at
      const serializedAcceptedAt = acceptedAt ? dayjs(acceptedAt).toISOString() : null
      this.savingAcceptedAt[assignment.id] = true

      try {
        await this.contractStore.updateGeneralTermAssignment(assignment.id, {
          accepted_at: serializedAcceptedAt,
        })
        assignment.accepted_at = serializedAcceptedAt
        Helpers.emitter.emit('success', this.$t('contracts.general_term_accepted_at_updated'))
      } catch (error) {
        this.acceptedAtDrafts[assignment.id] = previousAcceptedAt ? new Date(previousAcceptedAt) : null
        Helpers.emitter.emit(
            'error',
            error.response?.data?.messages || error.response?.data?.message || error.message
        )
      } finally {
        this.savingAcceptedAt[assignment.id] = false
      }
    },
  }
}
</script>

<style scoped>
.accepted-at-cell {
  min-width: 220px;
}

.accepted-at-editor {
  position: relative;
}

.accepted-at-saving {
  position: absolute;
  right: 2.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}
</style>
